import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Gift, Lock, Maximize, Plus, Settings, Star } from "lucide-react";
import api, { money } from "@/lib/api";
import { QRCodeCanvas } from "qrcode.react";

function AdLoop({ media, imageSeconds, videoMode }) {
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);
  const item = media[idx % media.length];
  const itemId = item?.id;
  const next = useCallback(() => setIdx((i) => (i + 1) % media.length), [media.length]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (!item) return;
    if (item.resource_type === "image") timer.current = setTimeout(next, imageSeconds * 1000);
    return () => clearTimeout(timer.current);
  }, [itemId, imageSeconds, next]); // eslint-disable-line

  if (!item) return null;
  const inner = item.resource_type === "video" ? (() => {
    const repeatSame = videoMode === "single" || media.length === 1;
    const replay = (e) => { e.currentTarget.currentTime = 0; e.currentTarget.play().catch(() => {}); };
    return <video data-testid="display-video" src={item.url} autoPlay muted playsInline loop={repeatSame} onEnded={repeatSame ? replay : next} onError={next} className="w-full h-full object-contain" />;
  })() : <img data-testid="display-image" src={item.url} alt="" className="w-full h-full object-contain" />;
  return <div key={item.id} data-testid="display-ad-item" className="w-full h-full animate-in fade-in duration-700 ease-out">{inner}</div>;
}

function ThankYou({ url }) {
  return (
    <div data-testid="display-thank-you" className="w-full h-full flex items-center justify-center p-10 bg-metros-black animate-in fade-in duration-500">
      <div className="w-full max-w-3xl bg-white rounded-[2rem] p-10 md:p-14 shadow-2xl flex flex-col md:flex-row items-center gap-10">
        <div className="flex-1">
          <p className="text-sm uppercase tracking-[0.3em] text-gray-400 font-bold">See you soon</p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-metros-black mt-2">Thank you!</h1>
          <p className="text-base md:text-lg text-gray-500 mt-4">Not a member yet? Scan to join METROS Rewards and earn points on every order.</p>
        </div>
        <div className="bg-gray-50 rounded-2xl p-5 shrink-0">
          <QRCodeCanvas data-testid="display-rewards-qr" value={url} size={200} />
        </div>
      </div>
    </div>
  );
}

function CustomerCard({ ev }) {
  return (
    <div data-testid="display-customer-card" className="w-full h-full flex items-center justify-center p-10 bg-metros-black">
      <div className="w-full max-w-3xl bg-white rounded-[2rem] p-10 md:p-14 shadow-2xl animate-in fade-in zoom-in-95 duration-300">
        <p className="text-sm uppercase tracking-[0.3em] text-gray-400 font-bold">{ev.stage === "done" ? "Thank you" : "Welcome back"}</p>
        <h1 data-testid="display-customer-name" className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-metros-black mt-2 truncate">{ev.customer_name}</h1>
        <div className="mt-8 flex items-center gap-4">
          <div className="bg-metros-red text-white rounded-full p-4"><Star size={32} /></div>
          <div>
            <p className="text-gray-500 text-base md:text-lg">Points balance</p>
            <p data-testid="display-customer-points" className="font-heading font-extrabold text-5xl text-metros-red">{ev.points} pts</p>
          </div>
        </div>
        <div className="mt-8 grid grid-cols-2 gap-4">
          {ev.amount > 0 && <Stat label="Amount" value={money(ev.amount)} testId="display-amount" />}
          {ev.points_added > 0 && <Stat label="Points added" value={`+${ev.points_added} pts`} icon={<Plus size={18} />} testId="display-points-added" accent />}
          {ev.points_redeemed > 0 && <Stat label="Points redeemed" value={`-${ev.points_redeemed} pts`} icon={<Gift size={18} />} testId="display-points-redeemed" />}
          {ev.discount > 0 && <Stat label="Discount applied" value={`-${money(ev.discount)}`} testId="display-discount" accent />}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon, testId, accent }) {
  return (
    <div className={`rounded-2xl p-5 ${accent ? "bg-metros-red/10" : "bg-gray-100"}`}>
      <p className="text-xs uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1">{icon}{label}</p>
      <p data-testid={testId} className={`font-heading font-extrabold text-3xl mt-1 ${accent ? "text-metros-red" : "text-metros-black"}`}>{value}</p>
    </div>
  );
}

export default function Display() {
  const [params] = useSearchParams();
  const code = (params.get("code") || "").toUpperCase();
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");
  const [event, setEvent] = useState(null);
  const [thanks, setThanks] = useState(false);
  const prevRef = useRef(null);

  const thankSecs = useRef(8);
  useEffect(() => { thankSecs.current = config?.thank_you_seconds || 8; }, [config]);
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = event;
    if (prev && prev.stage === "done" && !event) {
      setThanks(true);
      const t = setTimeout(() => setThanks(false), thankSecs.current * 1000);
      return () => clearTimeout(t);
    }
    if (event) setThanks(false);
  }, [event]);

  useEffect(() => {
    if (!code) { setError("Missing display code"); return; }
    const load = () => api.get("/display/config", { params: { code } }).then(({ data }) => { setConfig(data); setError(""); }).catch(() => setError("Unknown display code"));
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [code]);

  useEffect(() => {
    if (!code) return;
    const poll = () => api.get("/display/state", { params: { code, _: Date.now() } }).then(({ data }) => setEvent(data.event || null)).catch(() => {});
    poll();
    const t = setInterval(poll, 1500);
    return () => clearInterval(t);
  }, [code]);

  const [unlocked, setUnlocked] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [pinErr, setPinErr] = useState("");
  const wakeRef = useRef(null);
  const [started, setStarted] = useState(() => window.matchMedia("(display-mode: fullscreen)").matches || window.matchMedia("(display-mode: standalone)").matches);
  const [isFs, setIsFs] = useState(!!document.fullscreenElement);
  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);
  const taps = useRef([]);

  const keepAwake = useCallback(async () => {
    if (!navigator.wakeLock || wakeRef.current) return;
    try { wakeRef.current = await navigator.wakeLock.request("screen"); wakeRef.current.addEventListener("release", () => { wakeRef.current = null; }); }
    catch (e) { console.warn("wake lock unavailable", e); }
  }, []);

  useEffect(() => {
    if (!started) return;
    keepAwake();
    const onVis = () => { if (document.visibilityState === "visible") keepAwake(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); wakeRef.current?.release?.(); wakeRef.current = null; };
  }, [started, keepAwake]);

  const submitPin = async (value) => {
    try {
      await api.post("/display/verify-pin", { code, pin: value });
      setPinOpen(false); setPin(""); setPinErr(""); setUnlocked(true);
    } catch { setPinErr("Incorrect PIN"); setPin(""); }
  };
  const pressKey = (k) => {
    if (k === "del") { setPin((p) => p.slice(0, -1)); return; }
    setPin((p) => (p + k).slice(0, 6)); setPinErr("");
  };

  useEffect(() => {
    let link = document.querySelector("link[rel=manifest]");
    const prev = link?.getAttribute("href");
    if (!link) { link = document.createElement("link"); link.rel = "manifest"; document.head.appendChild(link); }
    link.href = "/display-manifest.json";
    return () => { if (prev) link.href = prev; else link.remove(); };
  }, []);

  const start = async () => {
    setStarted(true);
    const el = document.documentElement;
    try { await (el.requestFullscreen?.({ navigationUI: "hide" }) || el.webkitRequestFullscreen?.()); } catch (e) { console.warn("fullscreen unavailable", e); }
    try { await window.screen?.orientation?.lock?.("landscape"); } catch { /* not supported */ }
  };

  useEffect(() => {
    if (!unlocked) return;
    const t = setTimeout(() => setUnlocked(false), 30000);
    return () => clearTimeout(t);
  }, [unlocked]);

  const cornerTap = () => {
    const now = Date.now();
    taps.current = [...taps.current.filter((t) => now - t < 3000), now];
    if (taps.current.length >= 5) { taps.current = []; if (config?.pin_required) { setPin(""); setPinErr(""); setPinOpen(true); } else setUnlocked(true); }
  };

  if (error) return <div data-testid="display-error" className="min-h-screen bg-metros-black text-white flex items-center justify-center text-xl font-bold">{error}</div>;
  if (!config) return <div className="min-h-screen bg-metros-black" />;
  return (
    <div data-testid="display-root" className={`h-screen w-screen overflow-hidden select-none relative ${unlocked ? "" : "pointer-events-none"}`} style={{ touchAction: unlocked ? "auto" : "none", WebkitTouchCallout: "none", WebkitUserSelect: "none" }} onContextMenu={(e) => e.preventDefault()}>
      <DisplayScreen config={config} event={event} thanks={thanks} />
      {!started && (
        <button data-testid="display-start-btn" onClick={start} className="absolute inset-0 pointer-events-auto bg-metros-black/90 text-white flex flex-col items-center justify-center gap-4 cursor-pointer">
          <Maximize size={48} className="text-metros-red" />
          <span className="font-heading font-extrabold text-3xl">Tap to start full screen</span>
          <span className="text-sm text-white/60">Tip: use "Add to Home Screen" for a permanent full-screen display</span>
        </button>
      )}
      {started && unlocked && !isFs && (
        <button data-testid="display-fullscreen-btn" onClick={start} className="absolute top-4 left-4 bg-white/90 text-metros-black rounded-full px-4 py-2 text-sm font-bold shadow-lg flex items-center gap-2 pointer-events-auto"><Maximize size={16} /> Full screen</button>
      )}
      {!unlocked && !pinOpen && <button aria-label="unlock" data-testid="display-unlock-corner" onPointerDown={cornerTap} className="absolute top-0 right-0 w-24 h-24 opacity-0 pointer-events-auto" />}
      {pinOpen && (
        <div data-testid="display-pin-pad" className="absolute inset-0 pointer-events-auto bg-metros-black/90 flex items-center justify-center p-6">
          <div className="bg-white rounded-3xl p-6 w-full max-w-xs shadow-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-gray-400 font-bold text-center">Staff PIN</p>
            <div data-testid="display-pin-dots" className="flex justify-center gap-3 my-4 h-4">
              {Array.from({ length: Math.max(4, pin.length) }).map((_, i) => <span key={i} className={`w-3.5 h-3.5 rounded-full ${i < pin.length ? "bg-metros-red" : "bg-gray-200"}`} />)}
            </div>
            {pinErr && <p data-testid="display-pin-error" className="text-xs text-metros-red font-semibold text-center -mt-2 mb-2">{pinErr}</p>}
            <div className="grid grid-cols-3 gap-2">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", "del", "0", "ok"].map((k) => (
                <button key={k} data-testid={`pin-key-${k}`} onClick={() => (k === "ok" ? submitPin(pin) : pressKey(k))} disabled={k === "ok" && pin.length < 4}
                  className={`h-14 rounded-2xl font-heading font-extrabold text-xl active:scale-95 transition-transform disabled:opacity-40 ${k === "ok" ? "bg-metros-red text-white" : k === "del" ? "bg-gray-100 text-gray-500 text-sm" : "bg-gray-100 text-metros-black"}`}>
                  {k === "del" ? "⌫" : k === "ok" ? "Unlock" : k}
                </button>
              ))}
            </div>
            <button data-testid="pin-cancel" onClick={() => { setPinOpen(false); setPin(""); }} className="w-full mt-3 text-sm font-semibold text-gray-500">Cancel</button>
          </div>
        </div>
      )}
      {unlocked && (
        <div data-testid="display-unlocked-bar" className="absolute top-4 right-4 flex gap-2 animate-in fade-in duration-300">
          <a data-testid="display-settings-btn" href="/staff/display" className="bg-white/90 text-metros-black rounded-full px-4 py-2 text-sm font-bold shadow-lg flex items-center gap-2"><Settings size={16} /> Settings</a>
          <button data-testid="display-lock-btn" onClick={() => setUnlocked(false)} className="bg-metros-black/80 text-white rounded-full px-4 py-2 text-sm font-bold shadow-lg flex items-center gap-2"><Lock size={16} /> Lock</button>
        </div>
      )}
    </div>
  );
}

function DisplayScreen({ config, event, thanks }) {
  if (event) return <CustomerCard ev={event} />;
  if (thanks) return <ThankYou url={`${window.location.origin}/register`} />;

  const media = config.media || [];
  return (
    <div data-testid="display-idle" className="h-full w-full overflow-hidden bg-black relative">
      {media.length > 0 ? <AdLoop media={media} imageSeconds={config.image_seconds || 8} videoMode={config.video_mode || "all"} /> : (
        <div className="w-full h-full flex items-center justify-center text-white/60 font-heading font-extrabold text-4xl sm:text-5xl">{config.branch_name || "METROS"}</div>
      )}
      {config.text_visible && config.text && (
        <div data-testid="display-text" className="absolute inset-x-0 bottom-0 bg-metros-black/85 backdrop-blur-md text-white px-10 py-6 text-center font-heading font-extrabold text-2xl md:text-4xl">{config.text}</div>
      )}
    </div>
  );
}
